import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFeatureMenu } from 'features/menu/hooks/use-feature-menu';
import { useStoreContext } from 'features/store';
import { normalizeAvailableDayParts } from 'features/store/utils/day-part-utils';
import { isEqual } from 'lodash-es';

import { IRestaurant } from '@rbi-ctg/store';
import { isAppMenuVariant } from 'experiments/menu-variant/is-app-menu-variant';
import { useLogMenuExperimentExposure } from 'experiments/menu-variant/use-log-menu-experiment-exposure';
import { useMenuExperimentVariant } from 'experiments/menu-variant/use-menu-experiment-variant-name';
import {
  IConfigOfferEntryDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  useOneStoreMenuMetadataQuery,
} from 'generated/graphql-gateway';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useInterval from 'hooks/use-interval';
import { DEFAULT_MENU_VARIANT_KEY, MENU_API_URL } from 'remote/constants';
import { IDayPartBoundary, IValidDayPart } from 'state/menu/main-menu/types';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { appVersion } from 'utils/app-version-info';
import { getDateInTimezone } from 'utils/dateTime';
import { getLanguage } from 'utils/intl/language';
import logger from 'utils/logger';
import { fetch } from 'utils/network';
import { startTrace } from 'utils/performance/trace';
import { PerformanceTrace } from 'utils/performance/types';

import { applyLocaleTextTransform } from './apply-locale-text-transform';
import { findActiveDayParts, getDayPartsForDate } from './dayparts';
import {
  IIngredients,
  ILocalizedMenu,
  ILocalizedMenuEntry,
  IMainMenuNode,
  IndexedMenu,
  PosServiceMode,
} from './types';
import { IUnifiedMenuContext, UnifiedMenuContext } from './unified-menu-context';
import {
  createIndexedMenu,
  createIndexedOffers,
  createIndexedRewards,
  createMainMenuNode,
} from './utils';

interface ILoadedMenuVariant {
  requestedVariantKey: string;
  resolvedVariantKey: string;
  storeNumber: string | null;
}

interface IPayloadDayPartSource {
  menu: ILocalizedMenu | undefined;
  shouldRefreshActiveDayParts: boolean;
  storeNumber: string | undefined;
  timezone: string | undefined;
}

interface IPayloadDayPartValues {
  activeDayParts: IDayPartBoundary[];
  dayParts: IValidDayPart[];
}

interface IPayloadDayPartSnapshot extends IPayloadDayPartValues {
  source: IPayloadDayPartSource;
}

const DAY_PART_REFRESH_INTERVAL = 60 * 1000;
const SANITY_VARIANT_VALIDATION_TIMEOUT_MS = 3 * 1000;
const EMPTY_DAY_PARTS: IDayPartBoundary[] = [];
const EMPTY_MENU_DAY_PARTS: IValidDayPart[] = [];

const derivePayloadDayParts = ({
  menu,
  shouldRefreshActiveDayParts,
  timezone,
}: IPayloadDayPartSource): IPayloadDayPartValues => {
  if (!menu) {
    return {
      activeDayParts: EMPTY_DAY_PARTS,
      dayParts: EMPTY_MENU_DAY_PARTS,
    };
  }

  const now = timezone ? getDateInTimezone(timezone) : new Date();
  const menuDayParts = menu.dayParts ?? [];
  return {
    activeDayParts: shouldRefreshActiveDayParts
      ? findActiveDayParts(menuDayParts, now)
      : EMPTY_DAY_PARTS,
    dayParts: getDayPartsForDate(menuDayParts, now),
  };
};

export { applyLocaleTextTransform } from './apply-locale-text-transform';

/** Provides the active One Menu, including an assigned Amplitude menu variant when available. */
export const UnifiedMenuProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isEnabled = useEnableUnifiedMenu();
  const { store, availableDayParts } = useStoreContext();
  const storeInfoActiveDayParts = normalizeAvailableDayParts(availableDayParts);
  const isMenuExperimentEnabled = isEnabled && Boolean(store?.number);
  const menuExperimentVariant = useMenuExperimentVariant({ isEnabled: isMenuExperimentEnabled });
  const isMenuExperimentPending = menuExperimentVariant?.isPending === true;
  const amplitudeVariantNames = isMenuExperimentPending
    ? []
    : menuExperimentVariant?.variantNames ?? [];
  const amplitudeVariantNamesKey = amplitudeVariantNames.join('\u0000');
  const { featureMenu, featureMenuLoading } = useFeatureMenu({
    isEnabled: isMenuExperimentEnabled && amplitudeVariantNames.length > 0,
  });
  const featureMenuRequestKey = [store?.number, getLanguage(), amplitudeVariantNamesKey].join(
    '\u0000'
  );
  const [timedOutFeatureMenuKey, setTimedOutFeatureMenuKey] = useState<string>();
  useEffect(() => {
    if (
      !featureMenuLoading ||
      !amplitudeVariantNamesKey ||
      timedOutFeatureMenuKey === featureMenuRequestKey
    ) {
      return;
    }

    const timeoutId = setTimeout(
      () => setTimedOutFeatureMenuKey(featureMenuRequestKey),
      SANITY_VARIANT_VALIDATION_TIMEOUT_MS
    );
    return () => clearTimeout(timeoutId);
  }, [amplitudeVariantNamesKey, featureMenuLoading, featureMenuRequestKey, timedOutFeatureMenuKey]);
  const isMenuSelectionPending =
    isMenuExperimentPending ||
    (amplitudeVariantNames.length > 0 &&
      featureMenuLoading &&
      timedOutFeatureMenuKey !== featureMenuRequestKey);
  const sanityVariantKeys = new Set(
    featureMenu?.variants?.map(variant => variant?.key?.current).filter(Boolean) ?? []
  );
  const sanityVariantNamesKey = [...sanityVariantKeys].join('\u0000');
  const sanityAppVariantKeys = new Set(
    featureMenu?.variants
      ?.filter(isAppMenuVariant)
      .map(variant => variant?.key?.current)
      .filter(Boolean) ?? []
  );
  const sanityAppVariantNamesKey = [...sanityAppVariantKeys].join('\u0000');
  const menuVariantNamesKey = amplitudeVariantNames
    .filter(name => sanityAppVariantKeys.has(name))
    .join('\u0000');
  const hasFeatureMenu = Boolean(featureMenu);
  useEffect(() => {
    if (hasFeatureMenu && !featureMenuLoading && amplitudeVariantNamesKey && !menuVariantNamesKey) {
      logger.warn({
        message:
          'Assigned menu variant is not eligible for app in the published Sanity Feature Menu',
        assignedVariantNames: amplitudeVariantNamesKey.split('\u0000'),
        sanityVariantKeys: sanityVariantNamesKey ? sanityVariantNamesKey.split('\u0000') : [],
        sanityAppVariantKeys: sanityAppVariantNamesKey
          ? sanityAppVariantNamesKey.split('\u0000')
          : [],
        storeNumber: store?.number,
      });
    }
  }, [
    amplitudeVariantNamesKey,
    featureMenuLoading,
    hasFeatureMenu,
    menuVariantNamesKey,
    sanityAppVariantNamesKey,
    sanityVariantNamesKey,
    store?.number,
  ]);
  const menuVariantKeys = useMemo(
    () => (menuVariantNamesKey ? menuVariantNamesKey.split('\u0000') : [DEFAULT_MENU_VARIANT_KEY]),
    [menuVariantNamesKey]
  );
  const menuVariantRequestKey = menuVariantKeys.join('\u0000');
  const menuVariantKey = menuVariantKeys[0];
  const { posServiceMode } = useDerivedServiceModeContext();
  const [hasError, setHasError] = useState(false);

  /**
   * Store the last fetch time for each store menu, used to determine if the menu if we need to refetch
   * the full menu or keep the cached version. This being empty means that the menu has never been fetched
   * in the current session.
   */
  const lastFetchTime = useRef<Record<string, number>>({});
  const fetchId = useRef(0);

  const [menu, setMenu] = useState<ILocalizedMenu | undefined>(undefined);
  const [loadedStoreNumber, setLoadedStoreNumber] = useState<string | null>(null);
  const [loadedMenuVariant, setLoadedMenuVariant] = useState<ILoadedMenuVariant>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [payloadDayPartSnapshot, setPayloadDayPartSnapshot] = useState<IPayloadDayPartSnapshot>();
  const isCurrentStoreMenuLoaded = Boolean(
    isEnabled &&
      !isMenuSelectionPending &&
      menu &&
      loadedStoreNumber === (store?.number ?? null) &&
      loadedMenuVariant?.requestedVariantKey === menuVariantRequestKey &&
      loadedMenuVariant.storeNumber === (store?.number ?? null)
  );
  const currentMenu = isCurrentStoreMenuLoaded ? menu : undefined;
  const isCurrentStoreMenuLoading = Boolean(
    isEnabled && store?.number && !isMenuSelectionPending && !hasError && !isCurrentStoreMenuLoaded
  );
  const metadataMenuVariantKey =
    loadedMenuVariant?.requestedVariantKey === menuVariantRequestKey &&
    loadedMenuVariant.storeNumber === (store?.number ?? null)
      ? loadedMenuVariant.resolvedVariantKey
      : menuVariantKey;

  // Get the last modified time of the menu and refetch if it has changed
  useOneStoreMenuMetadataQuery({
    variables: store?.number
      ? { storeId: store.number, variantName: metadataMenuVariantKey }
      : undefined,
    skip: !isEnabled || !store?.number || isMenuSelectionPending,
    onCompleted: data => {
      if (data.OneStoreMenuMetadata?.lastModified) {
        const lastModified = Date.parse(data.OneStoreMenuMetadata.lastModified);
        if (lastModified > lastFetchTime.current[store?.number ?? '']) {
          fetchMenu();
        }
      }
    },
  });

  const indexedMenu: IndexedMenu<ILocalizedMenuEntry> = useMemo(
    () => (currentMenu ? createIndexedMenu(currentMenu) : IndexedMenu.EMPTY),
    [currentMenu]
  );

  const indexedRewards: IndexedMenu<IRewardEntryDto> = useMemo(
    () => (currentMenu ? createIndexedRewards(currentMenu) : IndexedMenu.EMPTY),
    [currentMenu]
  );
  const indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto> = useMemo(
    () => (currentMenu ? createIndexedOffers(currentMenu) : IndexedMenu.EMPTY),
    [currentMenu]
  );

  const shouldRefreshPayloadDayParts =
    isCurrentStoreMenuLoaded && storeInfoActiveDayParts === undefined;
  const payloadDayPartSource = useMemo<IPayloadDayPartSource>(
    () => ({
      menu: currentMenu,
      shouldRefreshActiveDayParts: shouldRefreshPayloadDayParts,
      storeNumber: store?.number ?? undefined,
      timezone: store?.timezone ?? undefined,
    }),
    [currentMenu, shouldRefreshPayloadDayParts, store?.number, store?.timezone]
  );
  const derivedPayloadDayParts = useMemo(() => derivePayloadDayParts(payloadDayPartSource), [
    payloadDayPartSource,
  ]);
  const isPayloadDayPartSnapshotCurrent = payloadDayPartSnapshot?.source === payloadDayPartSource;
  const payloadActiveDayParts = isPayloadDayPartSnapshotCurrent
    ? payloadDayPartSnapshot.activeDayParts
    : derivedPayloadDayParts.activeDayParts;
  const payloadDayParts = isPayloadDayPartSnapshotCurrent
    ? payloadDayPartSnapshot.dayParts
    : derivedPayloadDayParts.dayParts;

  const refreshPayloadDayParts = useCallback(() => {
    if (!payloadDayPartSource.menu) {
      return;
    }

    const nextPayloadDayParts = derivePayloadDayParts(payloadDayPartSource);
    setPayloadDayPartSnapshot(currentSnapshot => {
      const currentPayloadDayParts =
        currentSnapshot?.source === payloadDayPartSource ? currentSnapshot : derivedPayloadDayParts;
      if (
        isEqual(currentPayloadDayParts.activeDayParts, nextPayloadDayParts.activeDayParts) &&
        isEqual(currentPayloadDayParts.dayParts, nextPayloadDayParts.dayParts)
      ) {
        return currentSnapshot;
      }

      return {
        ...nextPayloadDayParts,
        source: payloadDayPartSource,
      };
    });
  }, [derivedPayloadDayParts, payloadDayPartSource]);
  useInterval(refreshPayloadDayParts, currentMenu ? DAY_PART_REFRESH_INTERVAL : null);
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  useEffect(() => {
    if (!currentMenu) {
      return;
    }

    addAppStateChangeListeners(refreshPayloadDayParts);
    return removeAppStateChangeListeners;
  }, [
    addAppStateChangeListeners,
    refreshPayloadDayParts,
    removeAppStateChangeListeners,
    currentMenu,
  ]);

  const activeDayParts = !isEnabled
    ? EMPTY_DAY_PARTS
    : storeInfoActiveDayParts ??
      (shouldRefreshPayloadDayParts ? payloadActiveDayParts : EMPTY_DAY_PARTS);
  const isDayPartsSourceResolved = Boolean(
    isEnabled &&
      store?.number &&
      (storeInfoActiveDayParts !== undefined || isCurrentStoreMenuLoaded)
  );

  const mainMenu = useMemo(
    () =>
      maybeCreateMainMenu(
        currentMenu,
        indexedMenu,
        posServiceMode,
        store,
        activeDayParts,
        isDayPartsSourceResolved
      ),
    [activeDayParts, currentMenu, indexedMenu, isDayPartsSourceResolved, posServiceMode, store]
  );
  const isExperimentVariantDisplayed = Boolean(
    isCurrentStoreMenuLoaded &&
      !isLoading &&
      mainMenu?.children.length &&
      menuExperimentVariant &&
      (amplitudeVariantNames.length > 0
        ? amplitudeVariantNames.includes(loadedMenuVariant?.resolvedVariantKey ?? '')
        : loadedMenuVariant?.resolvedVariantKey === DEFAULT_MENU_VARIANT_KEY)
  );
  useLogMenuExperimentExposure({
    exposureFlagKey: menuExperimentVariant?.exposureFlagKey,
    exposurePathname: menuExperimentVariant?.exposurePathname,
    isVariantDisplayed: isExperimentVariantDisplayed,
  });

  const fetchMenu = useCallback(async () => {
    let trace: PerformanceTrace | null = null;
    const currentFetchId = ++fetchId.current;
    const requestedStoreNumber = store?.number ?? null;
    try {
      setHasError(false);
      setIsLoading(true);

      // new high performance fetch that hits s3 directly which is pre BR compressed
      trace = startTrace(`fetch_1m_s3`, { storeId: store?.number ?? '' });
      const fetchMenuVariant = (variantKey: string): Promise<ILocalizedMenu> =>
        fetch<ILocalizedMenu>(`${MENU_API_URL}/${store?.number}-app-${variantKey}.json`, {
          method: 'GET',
          headers: {
            'x-ui-version': appVersion,
          },
          // unfortunately, this adds ~15ms in debug mode (less in release mode probably),
          // but it's better than updating every `.locale` in the app to be dynamic — see
          // applyLocaleTextTransform for why this isn't a single blind replace.
          textTransform: text => applyLocaleTextTransform(text, getLanguage()),
        });

      const variantKeys = new Set([...menuVariantKeys, DEFAULT_MENU_VARIANT_KEY]);
      let result: { menu: ILocalizedMenu; variantKey: string } | undefined;
      let lastError: unknown;

      for (const variantKey of variantKeys) {
        try {
          result = {
            menu: await fetchMenuVariant(variantKey),
            variantKey,
          };
          break;
        } catch (error) {
          if (currentFetchId !== fetchId.current) {
            return;
          }
          lastError = error;
        }
      }

      if (!result) {
        throw lastError;
      }

      // ignore stale responses from previous store changes
      if (currentFetchId !== fetchId.current) {
        return;
      }

      setMenu(result.menu);
      setLoadedStoreNumber(requestedStoreNumber);
      setLoadedMenuVariant({
        requestedVariantKey: menuVariantRequestKey,
        resolvedVariantKey: result.variantKey,
        storeNumber: requestedStoreNumber,
      });
      lastFetchTime.current[requestedStoreNumber ?? ''] = Date.now();
    } catch (error) {
      if (currentFetchId !== fetchId.current) {
        return;
      }
      setHasError(true);
      setMenu(undefined);
      setLoadedStoreNumber(null);
      setLoadedMenuVariant(undefined);
    } finally {
      trace?.stop();
      if (currentFetchId === fetchId.current) {
        setIsLoading(false);
      }
    }
  }, [menuVariantKeys, menuVariantRequestKey, store?.number]);

  useEffect(() => {
    if (isEnabled && store?.number && !isMenuSelectionPending) {
      fetchMenu();
    }
  }, [isEnabled, isMenuSelectionPending, store?.number, fetchMenu]);

  useEffect(() => {
    if (!isEnabled || isMenuSelectionPending) {
      // Invalidate a pending One Menu request so it cannot repopulate state while this
      // provider is inactive or the menu experiment assignment is unresolved.
      fetchId.current += 1;
      setIsLoading(false);
    }
    setMenu(undefined);
    setLoadedStoreNumber(null);
    setLoadedMenuVariant(undefined);
    setHasError(false);
  }, [isEnabled, isMenuSelectionPending, menuVariantRequestKey, store?.number]);

  const value: IUnifiedMenuContext = {
    activeDayParts,
    dayParts: currentMenu ? payloadDayParts : EMPTY_MENU_DAY_PARTS,
    rawMenu: currentMenu,
    mainMenu: mainMenu?.children ?? EMPTY_MENU,
    indexedMenu,
    indexedRewards,
    indexedOffers,
    loadedStoreNumber: isCurrentStoreMenuLoaded ? loadedStoreNumber : null,
    isDayPartsSourceResolved,
    isLoading: isLoading || isMenuSelectionPending || isCurrentStoreMenuLoading,
    refetchMenu: fetchMenu,
    ingredients: (currentMenu?.ingredients as IIngredients[]) ?? [],
    hasError,
  };

  return <UnifiedMenuContext.Provider value={value}>{children}</UnifiedMenuContext.Provider>;
};

const EMPTY_MENU: IMainMenuNode[] = [];

function maybeCreateMainMenu(
  menu: ILocalizedMenu | undefined,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry> | undefined,
  posServiceMode: PosServiceMode | null,
  store: IRestaurant | undefined,
  activeDayParts: IDayPartBoundary[],
  isDayPartsSourceResolved: boolean
): IMainMenuNode | undefined {
  if (indexedMenu && menu && posServiceMode && store) {
    return createMainMenuNode(indexedMenu, menu.rootMenuEntryId, {
      posServiceMode,
      dayPartIds: isDayPartsSourceResolved ? activeDayParts.map(dayPart => dayPart.key) : undefined,
    });
  }
  return undefined;
}
